import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { API_URL } from '../api/base';
import { getLocalized } from '../utils/localized';

type LocText = { ru?: string; ky?: string; en?: string } | string | null;

interface Post {
  id: number;
  slug: string;
  title: LocText;
  excerpt: LocText;
  coverImage: string | null;
  publishedAt: string | null;
  category?: string | null;
  isFeatured?: boolean;
}

interface CategoryStat {
  category: string;
  count: number;
}

const PAGE_SIZE = 12;

const BlogList: React.FC = () => {
  const { t, i18n } = useTranslation();
  const [posts, setPosts] = useState<Post[]>([]);
  const [loading, setLoading] = useState(true);
  const [offset, setOffset] = useState(0);
  const [hasMore, setHasMore] = useState(true);
  const [category, setCategory] = useState<string>('all');
  const [categoryStats, setCategoryStats] = useState<CategoryStat[]>([]);

  // Загружаем список категорий один раз
  useEffect(() => {
    (async () => {
      try {
        const { data } = await axios.get<{ categories: CategoryStat[] }>(
          `${API_URL}/api/blog/categories`
        );
        setCategoryStats(data.categories || []);
      } catch (err) {
        console.error(err);
      }
    })();
  }, []);

  // Загружаем посты при смене категории
  useEffect(() => {
    (async () => {
      setLoading(true);
      try {
        const params: Record<string, string | number> = { limit: PAGE_SIZE, offset: 0 };
        if (category !== 'all') params.category = category;
        const { data } = await axios.get<{ posts: Post[] }>(`${API_URL}/api/blog`, { params });
        setPosts(data.posts);
        setHasMore(data.posts.length >= PAGE_SIZE);
        setOffset(data.posts.length);
      } catch (err) {
        console.error(err);
      } finally {
        setLoading(false);
      }
    })();
  }, [category]);

  const loadMore = async () => {
    try {
      const params: Record<string, string | number> = { limit: PAGE_SIZE, offset };
      if (category !== 'all') params.category = category;
      const { data } = await axios.get<{ posts: Post[] }>(`${API_URL}/api/blog`, { params });
      setPosts(prev => [...prev, ...data.posts]);
      setHasMore(data.posts.length >= PAGE_SIZE);
      setOffset(offset + data.posts.length);
    } catch (err) {
      console.error(err);
    }
  };

  const categories = useMemo(
    () => ['all', ...categoryStats.map(c => c.category)],
    [categoryStats]
  );

  // Featured — первый с isFeatured=true, иначе первый вообще
  const featured = useMemo(
    () => posts.find(p => p.isFeatured) || posts[0],
    [posts]
  );
  const rest = useMemo(
    () => (featured ? posts.filter(p => p.id !== featured.id) : posts),
    [posts, featured]
  );

  const fmtDate = (s: string | null) => {
    if (!s) return '';
    return new Date(s).toLocaleDateString(i18n.language === 'en' ? 'en-GB' : 'ru-RU', {
      day: 'numeric',
      month: 'long',
      year: 'numeric'
    });
  };

  const title = (p: Post) =>
    typeof p.title === 'string' ? p.title : getLocalized(p.title, i18n.language);
  const excerpt = (p: Post) =>
    typeof p.excerpt === 'string' ? p.excerpt : getLocalized(p.excerpt, i18n.language);

  return (
    <>
      <Header />
      <main style={{ maxWidth: 1100, margin: '0 auto', padding: '40px 24px 90px' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 14, marginBottom: 26 }}>
          <Link to="/" className="back-btn">
            ← {t('blog.back_to_home', 'На главную')}
          </Link>
          <span style={{ font: "400 14px 'Golos Text', sans-serif", color: '#9aa88c' }}>
            {t('blog.breadcrumb', 'Главная · Блог')}
          </span>
        </div>

        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'flex-end',
            gap: 20,
            flexWrap: 'wrap',
            marginBottom: 28
          }}
        >
          <div>
            <div style={{ marginBottom: 12 }}>
              <span className="section-tag">{t('blog.tag', 'Журнал проекта')}</span>
            </div>
            <h1
              style={{
                font: "700 34px/1.2 'Montserrat Alternates', sans-serif",
                color: '#2D5016',
                margin: 0
              }}
            >
              {t('blog.title')}
            </h1>
            <p
              style={{
                font: "400 16px/1.5 'Golos Text', sans-serif",
                color: '#5a6b4d',
                margin: '8px 0 0',
                maxWidth: 560
              }}
            >
              {t('blog.subtitle')}
            </p>
          </div>
        </div>

        {/* filters */}
        {categories.length > 1 && (
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 28 }}>
            {categories.map(c => (
              <button
                key={c}
                type="button"
                onClick={() => setCategory(c)}
                className={`seg-btn ${category === c ? 'active' : ''}`}
              >
                {c === 'all' ? t('blog.filter_all', 'Все') : c}
              </button>
            ))}
          </div>
        )}

        {loading ? (
          <div style={{ padding: '80px 0', textAlign: 'center', color: '#5a6b4d' }}>
            {t('loading', 'Загрузка…')}
          </div>
        ) : posts.length === 0 ? (
          <EmptyState />
        ) : (
          <>
            {/* featured */}
            {featured && (
              <Link
                to={`/blog/${featured.slug}`}
                className="blog-card blog-featured"
                style={{ marginBottom: 24 }}
              >
                <div style={{ flex: 1.1, minHeight: 300, position: 'relative' }}>
                  {featured.coverImage ? (
                    <img
                      src={featured.coverImage.startsWith('http') ? featured.coverImage : `${API_URL}${featured.coverImage}`}
                      alt=""
                      style={{
                        position: 'absolute',
                        inset: 0,
                        width: '100%',
                        height: '100%',
                        objectFit: 'cover'
                      }}
                    />
                  ) : (
                    <div style={{ position: 'absolute', inset: 0, background: '#DFEFCB' }} />
                  )}
                </div>
                <div
                  style={{
                    flex: 1,
                    padding: '34px 38px',
                    display: 'flex',
                    flexDirection: 'column',
                    justifyContent: 'center'
                  }}
                >
                  <div
                    style={{
                      display: 'flex',
                      gap: 8,
                      alignItems: 'center',
                      marginBottom: 14
                    }}
                  >
                    {featured.category && <span className="chip">{featured.category}</span>}
                    <span style={{ font: "400 13px 'Golos Text', sans-serif", color: '#9aa88c' }}>
                      {fmtDate(featured.publishedAt)}
                    </span>
                  </div>
                  <div style={{ font: "700 26px/1.25 'Montserrat Alternates', sans-serif", color: '#2D5016' }}>
                    {title(featured)}
                  </div>
                  {excerpt(featured) && (
                    <p
                      style={{
                        font: "400 15px/1.6 'Golos Text', sans-serif",
                        color: '#5a6b4d',
                        margin: '12px 0 0'
                      }}
                    >
                      {excerpt(featured)}
                    </p>
                  )}
                  <span
                    style={{
                      marginTop: 20,
                      font: "600 15px 'Golos Text', sans-serif",
                      color: '#0B7A43'
                    }}
                  >
                    {t('blog.read_more', 'Читать статью')} →
                  </span>
                </div>
              </Link>
            )}

            {/* grid */}
            <div className="grid-3" style={{ gap: 24 }}>
              {rest.map(p => (
                <Link key={p.slug} to={`/blog/${p.slug}`} className="blog-card">
                  <div style={{ position: 'relative', aspectRatio: '16 / 10' }}>
                    {p.coverImage ? (
                      <img
                        src={p.coverImage.startsWith('http') ? p.coverImage : `${API_URL}${p.coverImage}`}
                        alt=""
                        style={{
                          position: 'absolute',
                          inset: 0,
                          width: '100%',
                          height: '100%',
                          objectFit: 'cover'
                        }}
                      />
                    ) : (
                      <div style={{ position: 'absolute', inset: 0, background: '#DFEFCB' }} />
                    )}
                  </div>
                  <div style={{ padding: '20px 22px', display: 'flex', flexDirection: 'column', flex: 1 }}>
                    <div
                      style={{
                        display: 'flex',
                        gap: 8,
                        alignItems: 'center',
                        marginBottom: 12
                      }}
                    >
                      {p.category && <span className="chip">{p.category}</span>}
                      <span style={{ font: "400 12.5px 'Golos Text', sans-serif", color: '#9aa88c' }}>
                        {fmtDate(p.publishedAt)}
                      </span>
                    </div>
                    <div style={{ font: "700 18px/1.3 'Montserrat Alternates', sans-serif", color: '#2D5016' }}>
                      {title(p)}
                    </div>
                    {excerpt(p) && (
                      <p
                        style={{
                          font: "400 14px/1.55 'Golos Text', sans-serif",
                          color: '#5a6b4d',
                          margin: '10px 0 0',
                          flex: 1
                        }}
                      >
                        {excerpt(p)}
                      </p>
                    )}
                    <span
                      style={{
                        marginTop: 16,
                        font: "600 14px 'Golos Text', sans-serif",
                        color: '#0B7A43'
                      }}
                    >
                      {t('blog.read_more_short', 'Читать')} →
                    </span>
                  </div>
                </Link>
              ))}
            </div>

            {hasMore && (
              <div style={{ textAlign: 'center', marginTop: 38 }}>
                <button className="back-btn" style={{ padding: '13px 30px' }} onClick={loadMore}>
                  {t('donations.load_more', 'Загрузить ещё')}
                </button>
              </div>
            )}
          </>
        )}
      </main>
      <Footer />
    </>
  );
};

const EmptyState: React.FC = () => {
  const { t } = useTranslation();
  return (
    <div
      style={{
        background: '#fff',
        borderRadius: 28,
        boxShadow: '0 6px 24px rgba(45,80,22,.08)',
        padding: '80px 24px',
        textAlign: 'center',
        border: '2px dashed #CFE6B4'
      }}
    >
      <div
        style={{
          width: 110,
          height: 110,
          margin: '0 auto 24px',
          borderRadius: '50%',
          background: '#DFEFCB',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center'
        }}
      >
        <svg width="56" height="56" viewBox="0 0 40 40" fill="none">
          <path
            d="M14.675 28.06c3.27 0 5.925-2.55 5.925-5.7 0-3.15-2.655-5.7-5.925-5.7s-5.925 2.55-5.925 5.7c0 3.15 2.655 5.7 5.925 5.7Z"
            fill="#B7DE93"
          />
          <path
            d="M20.825 19.925c3.21 0 5.812-2.5 5.812-5.588 0-3.085-2.602-5.587-5.812-5.587-3.21 0-5.813 2.502-5.813 5.587 0 3.087 2.603 5.588 5.813 5.588Z"
            fill="#8FCB63"
          />
          <path
            d="M18.84 32c.11-1.5-.11-3.075-.34-4.725-.225-1.65-.525-3.337-.525-5.062 0-1.725.225-3.488.825-5.1.562-1.612 1.425-3.075 2.437-4.388-.862 1.425-1.5 2.963-1.837 4.575-.338 1.575-.338 3.225-.113 4.8.225 1.575.675 3.15 1.163 4.762.45 1.613.937 3.3 1.05 5.138H18.84Z"
            fill="#947151"
          />
        </svg>
      </div>
      <div style={{ font: "700 24px 'Montserrat Alternates', sans-serif", color: '#2D5016' }}>
        {t('blog.empty_title', 'Пока нет статей')}
      </div>
      <p
        style={{
          font: "400 16px/1.6 'Golos Text', sans-serif",
          color: '#5a6b4d',
          maxWidth: 420,
          margin: '12px auto 0'
        }}
      >
        {t('blog.empty_text', 'Мы готовим первые материалы о посадках и фестивалях. Загляните чуть позже.')}
      </p>
      <div style={{ display: 'flex', gap: 10, justifyContent: 'center', marginTop: 26, flexWrap: 'wrap' }}>
        <Link to="/" className="pill-btn">
          {t('blog.back_to_home', 'На главную')}
        </Link>
      </div>
    </div>
  );
};

export default BlogList;
